/* eslint-disable react/prop-types */
/**
 * Editors + the shared warning banner for the app's bounded share fields — the
 * interest fractions (0–1) and the deal-split percent (0–100). Both are "a
 * portion of a whole", so both get the same treatment:
 *
 *   - the matching bounds rule from utils/interestBounds, which makes
 *     `form.validateFields()` reject so Save is blocked, and
 *   - ONE full-width `<Alert>` at the top of the section listing every
 *     out-of-range field, in the same style as the Entities tab's
 *     "% Deal Receiving totals 34% — should total 100%" banner.
 *
 * The fields themselves stay quiet — red border only, no inline message — so a
 * single failure produces a single message instead of a banner plus a red
 * restatement plus a format hint. The rules keep their full text, which is
 * still what the save handlers' aggregate "couldn't save" Alert reads.
 *
 * GETTING THE BANNER FULL WIDTH AT THE TOP OF A SECTION
 * ----------------------------------------------------
 * Where the app owns the layout (the Deal tab renders its own category Cards;
 * the deal drawer owns its body) the host just renders `<ShareBoundsBanner>`.
 *
 * Where the LIBRARY owns the layout — `WigMetadataCrudPanel`'s Add/Edit modal
 * renders its own category sections — a customRenderer's output is wrapped in a
 * `<Col span={24/columns}>`, so rendering the Alert from a field renderer puts
 * it at a third of the row width and shoves that field's label out of
 * alignment. `withShareBoundsNotice` solves that with the library's own
 * full-width rule instead: it appends a synthetic metadata row whose
 * `data_type: "LongText"` earns `span 24` from WigMetadataForm, ordered just
 * ahead of the first guarded field so it lands at the top of that field's
 * section. `hide: true` keeps it out of the grid columns
 * (`metadataToAntdColumns` filters hidden rows) while the returned
 * `formFieldFilter` admits it to the form. It renders no `Form.Item`, so no
 * value is ever registered and nothing leaks into the request body.
 *
 * A `formNoticeSlot` prop on WigMetadataCrudPanel would make that unnecessary —
 * worth raising with component-agent, at which point the synthetic row can go.
 *
 * Deliberately NOT `max` on the InputNumber: AntD clamps a max on blur, which
 * would silently rewrite 80 to 1 (or 134 to 100) instead of telling the user
 * that what they typed was the wrong scale.
 */
import { Alert, Form, InputNumber } from "antd";

import {
  MAX_INTEREST,
  MAX_PERCENT,
  interestBoundsRule,
  pctBoundsRule,
} from "../utils/interestBounds";

// Interest fractions display at the land-standard 8 decimal places, matching
// `interestFmt` in utils/metadataV2Columns.js and numeric(10,8) in Postgres.
const NRI_PRECISION = 8;
const NRI_STEP = 0.00000001;
// Percents are entered whole-ish; the stored 0–1 fraction has 8 dp, which
// leaves 6 dp of percent.
const PCT_PRECISION = 6;

const INTEREST_SPEC = {
  max: MAX_INTEREST,
  unit: "",
  precision: NRI_PRECISION,
  step: NRI_STEP,
  hint: "a decimal fraction of 8/8ths, not a percent",
  placeholder: "0.00000000",
  extra: `Decimal fraction of 8/8ths — 0 to ${MAX_INTEREST}, 8 decimal places.`,
  rule: interestBoundsRule,
  defaultLabel: "Delivered NRI",
};

const PCT_SPEC = {
  max: MAX_PERCENT,
  unit: "%",
  precision: PCT_PRECISION,
  step: 1,
  hint: "a single row cannot take more than the whole deal",
  placeholder: "0",
  extra: `Percent of the deal — 0 to ${MAX_PERCENT} (enter 60 for 60%). Each side should total 100%.`,
  rule: pctBoundsRule,
  defaultLabel: "% Deal Receiving",
};

// Every guarded field, in the order failures are listed.
const GUARDED = [
  { field: "delivered_nri", label: "Delivered NRI", spec: INTEREST_SPEC },
  { field: "lessor_royalty", label: "Lessor Royalty", spec: INTEREST_SPEC },
  { field: "other_burdens", label: "Other Burdens (ORRIs)", spec: INTEREST_SPEC },
  { field: "warwick_orri", label: "Warwick ORRI", spec: INTEREST_SPEC },
  { field: "allocation", label: "Allocation", spec: INTEREST_SPEC },
  { field: "wi", label: "WI", spec: INTEREST_SPEC },
  { field: "nri", label: "NRI", spec: INTEREST_SPEC },
  { field: "pct_deal_receiving", label: "% Deal Receiving", spec: PCT_SPEC },
];

const GUARD_BY_FIELD = new Map(GUARDED.map((g) => [g.field, g]));

export const GUARDED_SHARE_FIELDS = GUARDED.map((g) => g.field);

/** Field name of the synthetic full-width notice row. Never sent to the API. */
export const SHARE_NOTICE_FIELD = "__share_bounds_notice__";

// The metadata relabels some fields per context ("% Deal Contributing" on the
// Sellers panel), so the banner reads labels from here rather than guessing.
// Module-level because the banner and the fields it describes are rendered by
// sibling components with no shared state inside the library's form.
const liveLabels = new Map();
const registerLabel = (field, label) => {
  if (field && label) liveLabels.set(field, label);
};
const labelOf = (field) =>
  liveLabels.get(field) || GUARD_BY_FIELD.get(field)?.label || field;

const isOver = (value, max) =>
  value != null && value !== "" && Number(value) > max;

const describe = (field, value) => {
  const { spec } = GUARD_BY_FIELD.get(field);
  const u = spec.unit;
  return `${labelOf(field)} is ${value}${u} — should be ${spec.max}${u} or less (${spec.hint}).`;
};

/**
 * One banner covering every out-of-range share field in the form. Red rather
 * than amber: unlike the Entities tab's advisory "doesn't total 100% yet"
 * notice, this one blocks the save.
 *
 * @param {object} props
 * @param {string[]} [props.fields] — fields to watch. Defaults to all guarded
 *   fields; one absent from the form simply never reports. Pass a subset to
 *   scope the banner to a single section.
 */
export const ShareBoundsBanner = ({ fields, style }) => {
  const watched = fields?.length ? fields : GUARDED_SHARE_FIELDS;
  // Hook count must be stable across renders, so watch the full guarded set and
  // filter afterwards rather than mapping over a variable-length list.
  const values = {};
  for (const g of GUARDED) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    values[g.field] = Form.useWatch(g.field);
  }

  const failing = GUARDED.filter(
    (g) => watched.includes(g.field) && isOver(values[g.field], g.spec.max)
  );
  if (!failing.length) return null;

  const messages = failing.map((g) => describe(g.field, values[g.field]));
  return (
    <Alert
      type="error"
      showIcon
      style={{ marginBottom: 12, ...style }}
      title={
        messages.length === 1 ? (
          messages[0]
        ) : (
          <>
            These values are outside their allowed range:
            <ul style={{ margin: "4px 0 0", paddingInlineStart: 20 }}>
              {messages.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </>
        )
      }
    />
  );
};

/**
 * A metadata-driven numeric field with an upper bound. A real component (rather
 * than JSX inlined into the renderer function) so `Form.useWatch` is hook-scoped
 * here instead of leaking into whichever form is doing the rendering.
 */
const BoundedField = ({ row, spec }) => {
  const field = row?.field;
  const label = row?.label || spec.defaultLabel;
  registerLabel(field, label);
  const value = Form.useWatch(field);
  const over = isOver(value, spec.max);

  const rules = [];
  if (row?.required === true) {
    rules.push({ required: true, message: `${label} is required` });
  }
  rules.push(spec.rule(label));

  // Format guidance lives in the label's tooltip (the ⓘ affordance the rest of
  // the metadata form already uses for `definition`), not in `extra`: an
  // always-visible grey line under every guarded field repeated the same
  // sentence 3-6 times per section, which read as noise rather than help.
  const tooltip = row?.definition ? (
    <>
      {row.definition}
      <br />
      <br />
      {spec.extra}
    </>
  ) : (
    spec.extra
  );

  return (
    <Form.Item
      name={field}
      label={label}
      tooltip={tooltip}
      rules={rules}
      // Over the ceiling the BANNER is the message: `help=""` suppresses AntD's
      // red restatement of the same rule. Other failures (negative,
      // non-numeric) fall through to the normal inline message.
      validateStatus={over ? "error" : undefined}
      help={over ? "" : undefined}
    >
      <InputNumber
        style={{ width: "100%" }}
        step={spec.step}
        precision={spec.precision}
        placeholder={row?.placeholder || spec.placeholder}
        status={over ? "error" : undefined}
      />
    </Form.Item>
  );
};

// Each map entry is a `customRenderers` value — a plain function the library
// CALLS with a metadata row, not a component it mounts, hence the lowercase
// name.
const makeRenderer = (spec) => {
  const renderBoundedField = (row) => (
    <BoundedField key={row?.field} row={row} spec={spec} />
  );
  return renderBoundedField;
};

const buildMap = (fields, spec) =>
  Object.fromEntries(fields.map((f) => [f, makeRenderer(spec)]));

const INTEREST_FIELDS = [
  // deals + approvals
  "delivered_nri",
  // lease_deed_tracts burdens — a royalty or ORRI is a fraction of 8/8ths
  // exactly like NRI, so a 20% royalty is 0.20.
  "lessor_royalty",
  "other_burdens",
  "warwick_orri",
  // well_tracts — per migration 018 these are "8-decimal fractions
  // (e.g. 0.18750000 for 3/16)".
  "allocation",
  "wi",
  "nri",
];

/** Guards for the interest fractions, keyed by metadata `field`. */
export const INTEREST_FIELD_RENDERERS = buildMap(INTEREST_FIELDS, INTEREST_SPEC);

/** Guard for the Buyer/Seller split on the Entities tab. */
export const PCT_FIELD_RENDERERS = buildMap(["pct_deal_receiving"], PCT_SPEC);

// Mirrors WigMetadataCrudPanel's own default form filter, so composing with it
// doesn't re-expose the audit columns it hides.
const PANEL_SERVER_MANAGED = new Set([
  "id",
  "created_at",
  "created_by",
  "updated_at",
  "updated_by",
]);
const panelDefaultFormFilter = (row) =>
  !row.hide && !PANEL_SERVER_MANAGED.has(row.field);

const renderShareNotice = () => <ShareBoundsBanner />;

/**
 * Augment a `WigMetadataCrudPanel`'s props so the share-bounds banner renders
 * full width at the top of the section holding the guarded fields.
 *
 * Returns `{ metadata, customRenderers, formFieldFilter }` to spread onto the
 * panel. A no-op (inputs returned unchanged) when the metadata contains no
 * guarded field, so it is safe to apply to any panel.
 *
 * @param {object} args
 * @param {Array}  args.metadata          — the metadata already filtered for the panel
 * @param {object} [args.customRenderers] — existing renderers to merge with
 * @param {Function} [args.formFieldFilter] — existing filter to compose with
 */
export const withShareBoundsNotice = ({
  metadata,
  customRenderers = {},
  formFieldFilter,
}) => {
  const rows = Array.isArray(metadata) ? metadata : [];
  const guarded = rows.filter((m) => GUARD_BY_FIELD.has(m?.field));
  if (!guarded.length) {
    return { metadata, customRenderers, formFieldFilter };
  }
  // Sit at the TOP of the section holding the guarded fields — ahead of every
  // row in that category, not just ahead of the first guarded one. (Anchoring
  // to the guarded field put the banner mid-section, below unrelated fields
  // like "Depths Covered".) The category is ordered by the smallest `ord` it
  // contains, so the 0.5 offset keeps the section itself in the same place.
  const first = guarded.reduce((a, b) => ((a.ord ?? 9999) <= (b.ord ?? 9999) ? a : b));
  const sectionTopOrd = Math.min(
    ...rows
      .filter((m) => (m?.category || null) === (first.category || null))
      .map((m) => m?.ord ?? 9999)
  );
  const noticeRow = {
    field: SHARE_NOTICE_FIELD,
    label: "",
    // LongText is what earns `span 24` (full row) from WigMetadataForm.
    data_type: "LongText",
    category: first.category,
    ord: sectionTopOrd - 0.5,
    // Hidden from the grid columns; admitted to the form by the filter below.
    hide: true,
    editable: true,
  };
  const baseFilter = formFieldFilter || panelDefaultFormFilter;
  return {
    metadata: [...rows, noticeRow],
    customRenderers: { ...customRenderers, [SHARE_NOTICE_FIELD]: renderShareNotice },
    formFieldFilter: (row) =>
      row?.field === SHARE_NOTICE_FIELD ? true : baseFilter(row),
  };
};

export const interestFieldRenderer = INTEREST_FIELD_RENDERERS.delivered_nri;
export const percentFieldRenderer = PCT_FIELD_RENDERERS.pct_deal_receiving;

export default ShareBoundsBanner;
